<script lang="ts" setup>
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { RegisterSettingStore } from '../../RegisterSettingStore';
import { storeToRefs } from 'pinia';
import SelectableField from '../SelectableField.vue';
import { LinkingBusinessHeader } from '../../interfaces/LinkingBusiness';
import { computed, ref, watch } from 'vue';
import { UNSELECTED_HEADER_VALUE } from '@/registerInputModule/constants';
const t = i18n.t;

const props = defineProps<{
  store: RegisterSettingStore;
}>();

const { state } = storeToRefs(props.store);
const selectedFieldId = ref<string>('');
const emits = defineEmits<{
  (e: 'on-click-next'): void;
  (e: 'on-click-back'): void;
  (e: 'on-click-use-previous-value'): void;
}>();

const imagesRequire = require.context('@/assets/img', false, /^\.\/register-input-step-flow-2.*\.png$/);
const localedRegisterInputStepFlowImage = ref(imagesRequire('./register-input-step-flow-2.png'));

watch(
  () => i18n.locale,
  (newVal) => {
    localedRegisterInputStepFlowImage.value =
      newVal === 'en' ? imagesRequire('./register-input-step-flow-2__en.png') : imagesRequire('./register-input-step-flow-2.png');
  },
  {
    immediate: true,
  }
);

/**
 * 選択されたフィールドを更新する
 * @param header - フィールド
 * @param updateValue - 更新する値
 */
const updateSelectedField = (header: LinkingBusinessHeader, updateValue: string) => {
  const beforeSelectedValue = state.value.linkingBusiness.linkedBusinessHeaders.find(
    (linkedHeader) => linkedHeader.key === header.key
  )?.value;

  // 選択肢から選択された値を削除
  state.value.uploadBusiness.businessHeaderCandidate = state.value.uploadBusiness.businessHeaderCandidate.map((candidate) => {
    if (candidate.value === updateValue) {
      return {
        ...candidate,
        alreadySelected: true,
      };
    }
    return candidate;
  });

  // 既に選択された値がある場合&選択された値とは違っていた場合、選択肢に復元する
  if (beforeSelectedValue && beforeSelectedValue !== updateValue) {
    state.value.uploadBusiness.businessHeaderCandidate = state.value.uploadBusiness.businessHeaderCandidate.map((candidate) => {
      if (candidate.value === beforeSelectedValue) {
        return {
          ...candidate,
          alreadySelected: false,
        };
      }
      return candidate;
    });
  }

  // 選択された値を更新
  state.value.linkingBusiness.linkedBusinessHeaders = state.value.linkingBusiness.linkedBusinessHeaders.map((linkedHeader) => {
    if (linkedHeader.key === header.key) {
      return {
        ...linkedHeader,
        value: updateValue !== UNSELECTED_HEADER_VALUE ? updateValue : undefined,
      };
    }
    return linkedHeader;
  });
};

/**
 * 戻るボタン
 */
const onClickBackButton = () => {
  emits('on-click-back');
};

/**
 * 次へボタン
 */
const onClickNextButton = () => {
  emits('on-click-next');
};

/**
 * 前回利用した値を使用ボタン
 */
const onClickUsePreviousValueButton = () => {
  emits('on-click-use-previous-value');
};

const selectedFieldSuggestions = computed(() => {
  return state.value.uploadBusiness.businessHeaderCandidate.map((candidate) => candidate.value);
});

/**
 * getter
 * @param header - ヘッダー
 */
const unusedLabel = (header: LinkingBusinessHeader) => {
  if (header.key === 'package' && header.unused) {
    return header.unused ? t('取引紐付け：文言不使用（数値）') : '';
  } else if (header.unused) {
    return header.unused ? t('取引紐付け：文言不使用') : '';
  } else {
    return '';
  }
};
</script>

<template>
  <div class="linking-business">
    <div class="linking-business__title">{{ t('取引紐付け：タイトル') }}</div>

    <img :src="localedRegisterInputStepFlowImage" class="linking-business__progress" />

    <div class="linking-business__sub-title sub-title">
      <div class="sub-title__prefix">
        <span> {{ t('取引紐付け：本文') }}</span>
        <template v-if="state.linkingBusiness.previousExcelInfo">
          <TextButton
            :button-style="'btn-primary'"
            :size="'btn-md'"
            :on-click="onClickUsePreviousValueButton"
            :disabled="false"
            class="bulk-action-button-container__export-button"
          >
            {{ t('取引紐付け：前回利用ボタン') }}
          </TextButton>
        </template>
      </div>
      <span class="sub-title__suffix"> {{ t('取引紐付け：注釈') }}</span>
    </div>

    <div class="linking-business__radio-button-container radio-button-container">
      <div class="radio-button-container__default-input">
        <input
          id="radio-button-container__button--default-input"
          v-model="state.linkingBusiness.useLinkingType"
          class="radio-button-container__button"
          type="radio"
          value="Default"
        />
        <label class="radio-button-container__label" for="radio-button-container__button--default-input">{{
          t('取引紐付け：ラジオボタン：デフォルト書式')
        }}</label>
      </div>

      <div class="radio-button-container__original-input">
        <input
          id="radio-button-container__button--original-input"
          v-model="state.linkingBusiness.useLinkingType"
          class="radio-button-container__button"
          type="radio"
          value="Original"
        />
        <label class="radio-button-container__label" for="radio-button-container__button--original-input">{{
          t('取引紐付け：ラジオボタン：オリジナル書式')
        }}</label>
      </div>
    </div>

    <div class="linking-business__linking-container linking-container">
      <template v-for="header in state.linkingBusiness.linkedBusinessHeaders" :key="`linking-business-header-${header.key}`">
        <div class="linking-container__at">
          <div class="linking-container__at--content">
            <div class="linking-container__at--label label-container">
              <span class="label-container__title">{{ t(header.displayName) + (header.required ? '*' : '') }}</span>
              <template v-if="header.unused != null">
                <input v-model="header.unused" type="checkbox" class="label-container__checkbox" />
                <span class="label-container__unused-label">{{ t('取引紐付け：チェックボックス：不使用') }}</span>
              </template>
            </div>
            <template v-if="!header.unused">
              <SelectableField
                :value="header.value"
                :selected-id="selectedFieldId"
                :hint="t('取引紐付け：サジェストヒント')"
                :suggestions="selectedFieldSuggestions"
                @update-value="updateSelectedField(header, $event)"
                @update-selected-id="(id) => (selectedFieldId = id)"
              />
            </template>
            <template v-else>
              <span class="linking-container__at--unused">{{ unusedLabel(header) }}</span>
            </template>
          </div>
          <span class="linking-container__at--error">{{
            header.validateError == 'NotSelected' ? t('取引紐付け：アラート文言：必須項目') : '　'
          }}</span>
        </div>
      </template>
    </div>

    <div class="linking-business__button-container">
      <TextButton
        :button-style="'btn-secondary'"
        :size="'btn-md'"
        :on-click="onClickBackButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：戻るボタン') }}
      </TextButton>
      <TextButton
        :button-style="'btn-primary'"
        :size="'btn-md'"
        :on-click="onClickNextButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：登録ボタン') }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.linking-business {
  display: grid;
  gap: 12px;

  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  border-radius: 4px;
  margin-top: 16px;
  padding: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__progress {
    justify-self: center;
  }

  &__button-container {
    display: grid;
    justify-self: center;
    gap: 8px;
    grid-template-columns: repeat(2, fit-content(100%));
    margin-top: 16px;
  }
}

.sub-title {
  display: grid;
  gap: 16px;
  grid-template-rows: repeat(2, fit-content(100%));

  &__prefix {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;

    display: grid;
    gap: 8px;
    grid-template-columns: max-content max-content;
    align-items: center;
  }

  &__suffix {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $textDefault;
  }
}

.radio-button-container {
  display: grid;
  grid-template-rows: repeat(2, fit-content(100%));
  gap: 16px;

  &__default-input {
    display: grid;
    grid-template-columns: repeat(2, fit-content(100%));
    align-items: end;
  }

  &__original-input {
    display: grid;
    grid-template-columns: repeat(2, fit-content(100%));
    align-items: end;
  }

  &__button {
    width: 18px;
    height: 18px;
  }

  &__label {
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
    color: $textDefault;
    padding: 0px 8px;
  }
}

.linking-container {
  display: grid;
  margin-top: 16px;
  gap: 32px;
  grid-template-columns: repeat(auto-fit, minmax(100px, 300px));

  &__at {
    display: grid;
    grid-template-rows: repeat(2, fit-content(100%));

    &--content {
      display: grid;
      grid-template-rows: repeat(2, fit-content(100%));
      border-bottom: 1px solid $borderDefault;
    }

    &--label {
      margin-bottom: 8px;
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $textDefault;
    }

    &--error {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticError;
    }

    &--unused {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticWarning;
    }
  }
}

.label-container {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  background-color: $semanticWhite;

  &__title {
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
    color: $textDefault;
  }

  &__checkbox {
    height: 16px;
    width: 16px;
  }

  &__unused-label {
    margin-left: 4px;
    padding-bottom: 4px;
    font-family: Noto Sans JP;
    font-size: $fontSizeSS;
    font-weight: 400;
    color: $textDefault;
  }
}
</style>
